'use client';

import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useStore } from '@base-ui/utils/store';
import Button from '@mui/material/Button';
import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import { isWeekend } from '@mui/x-scheduler-internals/use-adapter';
import { useAdapterContext } from '@mui/x-scheduler-internals/use-adapter-context';
import { CalendarGrid } from '@mui/x-scheduler-internals/calendar-grid';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-internals/use-event-calendar-store-context';
import { eventCalendarOccurrencePlaceholderSelectors, eventCalendarViewSelectors } from '@mui/x-scheduler-internals/event-calendar-selectors';
import { schedulerNowSelectors, schedulerOtherSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { DayGridEvent } from "../../internals/components/event/day-grid-event/DayGridEvent.mjs";
import { MoreEventsPopoverTrigger } from "../../internals/components/more-events-popover/MoreEventsPopover.mjs";
import { formatMonthAndDayOfMonth } from "../../internals/utils/date-utils.mjs";
import { isOccurrenceAllDayOrMultipleDay } from "../../internals/utils/event-utils.mjs";
import { EventDialogTrigger } from "../../internals/components/event-dialog/index.mjs";
import { useEventDialogContext } from "../../internals/components/event-dialog/EventDialog.mjs";
import { useEventCalendarStyledContext } from "../../event-calendar/EventCalendarStyledContext.mjs";
import { eventCalendarClasses } from "../../event-calendar/eventCalendarClasses.mjs";
import { EventSkeleton } from "../../internals/components/event-skeleton/index.mjs";
import { getCellFocusBackground } from "../../internals/utils/tokens.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const MonthViewCellRoot = styled(CalendarGrid.DayCell, {
  name: 'MuiEventCalendar',
  slot: 'MonthViewCell'
})(({
  theme
}) => ({
  display: 'grid',
  gridTemplateRows: 'repeat(var(--row-count), minmax(auto, 18px))',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5),
  fontSize: theme.typography.body2.fontSize,
  lineHeight: '18px',
  borderInlineStart: `1px solid transparent`,
  '&:not(:first-of-type)': {
    borderInlineStartColor: (theme.vars || theme).palette.divider
  },
  '&[data-weekend]': {
    backgroundColor: (theme.vars || theme).palette.action.hover,
    color: (theme.vars || theme).palette.text.primary
  },
  '&[data-current]': {
    backgroundColor: theme.alpha((theme.vars || theme).palette.primary.light, 0.05)
  },
  [`&[data-current] .${eventCalendarClasses.monthViewCellNumber}`]: {
    backgroundColor: (theme.vars || theme).palette.primary.main,
    color: (theme.vars || theme).palette.primary.contrastText
  },
  '&[data-other-month]': {
    color: (theme.vars || theme).palette.text.disabled
  },
  '&:focus-visible': {
    outline: 'none',
    backgroundColor: getCellFocusBackground(theme)
  },
  // Today button states
  [`&[data-current] > .${eventCalendarClasses.monthViewCellNumberButton} > .${eventCalendarClasses.monthViewCellNumber}`]: {
    backgroundColor: 'transparent'
  },
  [`&[data-current] > .${eventCalendarClasses.monthViewCellNumberButton}:hover > .${eventCalendarClasses.monthViewCellNumber}`]: {
    backgroundColor: 'transparent'
  },
  [`&[data-current] > .${eventCalendarClasses.monthViewCellNumberButton}:active > .${eventCalendarClasses.monthViewCellNumber}`]: {
    backgroundColor: 'transparent'
  },
  [`&[data-current] > .${eventCalendarClasses.monthViewCellNumberButton}`]: {
    backgroundColor: (theme.vars || theme).palette.primary.main,
    color: (theme.vars || theme).palette.primary.contrastText,
    marginTop: 1
  },
  [`&[data-current] > .${eventCalendarClasses.monthViewCellNumberButton}:hover`]: {
    backgroundColor: (theme.vars || theme).palette.primary.dark
  },
  [`&[data-current] > .${eventCalendarClasses.monthViewCellNumberButton}:active`]: {
    backgroundColor: (theme.vars || theme).palette.primary.dark
  }
}));
const MonthViewCellNumber = styled('span', {
  name: 'MuiEventCalendar',
  slot: 'MonthViewCellNumber'
})(({
  theme
}) => ({
  gridRow: 1,
  justifySelf: 'end',
  alignSelf: 'flex-end',
  padding: theme.spacing(0, 0.5),
  borderRadius: theme.shape.borderRadius
}));
const MonthViewCellNumberButton = styled('button', {
  name: 'MuiEventCalendar',
  slot: 'MonthViewCellNumberButton'
})(({
  theme
}) => ({
  gridRow: 1,
  justifySelf: 'end',
  width: 'fit-content',
  alignSelf: 'flex-end',
  borderRadius: theme.shape.borderRadius,
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  font: 'inherit',
  color: 'inherit',
  '&:hover': {
    backgroundColor: (theme.vars || theme).palette.action.hover
  },
  '&:active': {
    backgroundColor: (theme.vars || theme).palette.action.selected
  },
  '&:focus-visible': {
    backgroundColor: getCellFocusBackground(theme),
    outline: `2px solid ${(theme.vars || theme).palette.primary.main}`,
    outlineOffset: 2
  },
  '&:focus-visible:hover': {
    backgroundColor: (theme.vars || theme).palette.action.hover
  }
}));
const MonthViewCellEvents = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'MonthViewCellEvents'
})(({
  theme
}) => ({
  position: 'relative',
  display: 'grid',
  gap: theme.spacing(0.5)
}));
const MonthViewMoreEvents = styled(Button, {
  name: 'MuiEventCalendar',
  slot: 'MonthViewMoreEvents'
})(({
  theme
}) => ({
  margin: 0,
  color: (theme.vars || theme).palette.text.secondary,
  fontSize: theme.typography.caption.fontSize,
  lineHeight: '18px',
  paddingInlineStart: theme.spacing(0.5),
  justifyContent: 'flex-start',
  textTransform: 'none'
}));
const MonthViewPlaceholderEventContainer = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'MonthViewPlaceholderContainer'
})(({
  theme
}) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridTemplateRows: 'repeat(var(--row-count), minmax(auto, 18px))',
  gap: theme.spacing(0.5)
}));
export const MonthViewCell = /*#__PURE__*/React.forwardRef(function MonthViewCell(props, ref) {
  const {
    day,
    row,
    maxEvents,
    colIndex,
    ariaLabelledBy
  } = props;

  // Context hooks
  const adapter = useAdapterContext();
  const store = useEventCalendarStoreContext();
  const {
    classes,
    localeText
  } = useEventCalendarStyledContext();
  const {
    onOpen: startEditing
  } = useEventDialogContext();

  // Selector hooks
  const hasDayView = useStore(store, eventCalendarViewSelectors.hasDayView);
  const visibleDate = useStore(store, schedulerOtherSelectors.visibleDate);
  const isCreatingAnEvent = useStore(store, eventCalendarOccurrencePlaceholderSelectors.isCreatingInDayCell, day.value);
  const isToday = useStore(store, schedulerNowSelectors.isCurrentDay, day.value);
  const isLoading = useStore(store, schedulerOtherSelectors.isLoading);
  const placeholder = CalendarGrid.usePlaceholderInDay(day.value, row, maxEvents);

  // Ref hooks
  const cellRef = React.useRef(null);
  const handleRef = useMergedRefs(ref, cellRef);
  const isCurrentMonth = adapter.isSameMonth(day.value, visibleDate);
  const isFirstDayOfMonth = adapter.isSameDay(day.value, adapter.startOfMonth(day.value));
  const visibleOccurrences = day.withPosition.length > maxEvents ? day.withPosition.slice(0, maxEvents - 1) : day.withPosition;
  const hiddenCount = day.withPosition.length - visibleOccurrences.length;
  const cellNumberContent = /*#__PURE__*/_jsx(MonthViewCellNumber, {
    className: classes.monthViewCellNumber,
    children: isFirstDayOfMonth ? formatMonthAndDayOfMonth(day.value, adapter) : adapter.format(day.value, 'dayOfMonth')
  });

  // Day number header + max events
  const rowCount = 1 + maxEvents;
  React.useEffect(() => {
    if (!isCreatingAnEvent || !placeholder || !cellRef.current) {
      return;
    }
    startEditing(cellRef, placeholder);
  }, [isCreatingAnEvent, placeholder, startEditing]);
  return /*#__PURE__*/_jsxs(MonthViewCellRoot, {
    className: classes.monthViewCell,
    ref: handleRef,
    value: day.value,
    "aria-colindex": colIndex,
    "aria-labelledby": ariaLabelledBy,
    "data-current": isToday || undefined,
    "data-other-month": !isCurrentMonth || undefined,
    "data-weekend": isWeekend(adapter, day.value) || undefined,
    lockSurfaceType: true,
    style: {
      '--row-count': rowCount
    },
    children: [hasDayView ? /*#__PURE__*/_jsx(MonthViewCellNumberButton, {
      type: "button",
      className: classes.monthViewCellNumberButton,
      onClick: event => store.switchToDay(day.value, event),
      tabIndex: -1,
      children: cellNumberContent
    }) : cellNumberContent, /*#__PURE__*/_jsxs(MonthViewCellEvents, {
      className: classes.monthViewCellEvents,
      children: [isLoading && /*#__PURE__*/_jsx(EventSkeleton, {
        "data-variant": "day-grid"
      }), !isLoading && visibleOccurrences.map(occurrence => {
        if (occurrence.position.isInvisible) {
          return /*#__PURE__*/_jsx(DayGridEvent, {
            occurrence: occurrence,
            variant: "invisible"
          }, occurrence.key);
        }
        return /*#__PURE__*/_jsx(EventDialogTrigger, {
          occurrence: occurrence,
          children: /*#__PURE__*/_jsx(DayGridEvent, {
            occurrence: occurrence,
            variant: isOccurrenceAllDayOrMultipleDay(occurrence, adapter) ? 'filled' : 'compact'
          })
        }, occurrence.key);
      }), hiddenCount > 0 && /*#__PURE__*/_jsx(MoreEventsPopoverTrigger, {
        occurrences: day.withPosition,
        day: day,
        children: /*#__PURE__*/_jsx(MonthViewMoreEvents, {
          size: "small",
          "aria-label": localeText.hiddenEvents(hiddenCount),
          className: classes.monthViewMoreEvents,
          children: localeText.hiddenEvents(hiddenCount)
        })
      }), placeholder != null && /*#__PURE__*/_jsx(MonthViewPlaceholderEventContainer, {
        className: classes.monthViewPlaceholderContainer,
        children: /*#__PURE__*/_jsx(DayGridEvent, {
          occurrence: placeholder,
          variant: "placeholder"
        })
      })]
    })]
  }, day.key);
});
if (process.env.NODE_ENV !== "production") MonthViewCell.displayName = "MonthViewCell";